import { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import { ProjectContentTableOfContentTab } from '../types';
import { ItemsProps } from '../types';
import {
  gallery,
  goals,
  highlights,
  investors,
  marketStrategy,
  marketTrends,
  roadmap,
  solutions,
  streams,
  targetMarket,
  teamMembers,
  tokenUtility,
  tokenUtilityGallery,
} from './constants';

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
}

const SubTitle = ({ className, ...props }: ComponentPropsWithoutRef<'h3'>) => {
  return (
    <h3
      className={classNames('text-2xl font-semibold text-neutral-500 max-sm:text-base', className)}
      {...props}
    />
  );
};

const List = ({ items, description, className, descriptionClassName }: ListProps) => {
  return (
    <>
      {Boolean(description) && (
        <p
          className={classNames(
            'mb-4 sm:text-2xl font-semibold text-neutral-500',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
      <ul className={classNames('list-disc pl-6', className)}>
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id}>
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction and Highlights',
    content: (
      <>
        <SubTitle className="mb-4">Introduction</SubTitle>
        <p className="mb-6 max-sm:text-sm">
          Fanton is the first play-to-earn game in the Fantasy Football genre integrated into
          Telegram. Using hand-drawn NFTs based on the TON blockchain, players can create fantasy
          football teams and participate in tournaments to win prizes such as NFTs and Toncoin.
          Players' scores in the tournaments depend on the performance of footballers in real
          matches. The game taps into a $25 billion per year market with a 15% growth rate.
        </p>
        <List items={highlights} description="Key Highlights:" />
      </>
    ),
  },
  {
    id: 'product',
    title: 'Product',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm">
          Fanton is the first play-to-earn game in the Fantasy Football genre integrated into
          Telegram. The game uses hand-drawn NFTs based on the TON blockchain. Players can create
          fantasy football teams and participate in tournaments to win prizes such as NFTs and
          Toncoin. Players' scores in the tournaments depend on the performance of footballers in
          real matches.
        </p>
        <div className="overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-4/5 px-1')}>
                <ImageProxy
                  alt=""
                  width={1920}
                  height={1080}
                  quality={80}
                  className="w-full h-auto rounded-lg select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <SubTitle className="mb-4">Problem</SubTitle>
        <p className="mb-5 max-sm:text-sm">
          Fanton aims to bring a user-friendly crypto product to the mass market through Telegram
          Web Apps and a proven gameplay model. Existing Fantasy Football games are often difficult
          for new players to start, so Fanton focuses on accessibility and ease of use.
        </p>
        <SubTitle className="mb-4">Solution</SubTitle>
        <p className="mb-6 max-sm:text-sm">
          Fanton makes it easy for users to assemble a &quot;dream team&quot; of their favorite
          football players. If the players perform well in real matches, users win prizes, making
          the gaming experience more exciting.
        </p>
        <List items={solutions} />
      </>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model and Revenue Streams',
    content: (
      <>
        <p className="mb-3 max-sm:text-sm">
          Fanton operates on a freemium business model. The core game is free-to-play, but players
          can enter paid tournamensts for higher prizes. Revenue is primarily generated from:
        </p>
        <List items={streams} />
        <p className=" mt-3 max-sm:text-sm">
          This model attracts a large player base and engages them through a mix of free and paid
          content, leading to a sustainable and profitable revenue stream.
        </p>
      </>
    ),
  },
  {
    id: 'investors-and-partners',
    title: 'Investors and Partners',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
          {investors.map(({ name, image, className, width, height }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'marketing-strategy',
    title: 'Marketing Strategy',
    content: (
      <>
        <p className="mb-3">Market Trends:</p>
        <List items={marketTrends} className="mb-3" />
        <p className="mb-3">Target Market:</p>
        <List
          items={targetMarket}
          description="Our target market includes Fantasy Sports enthusiasts, Telegram users, and the broader TON ecosystem. We are targeting 126.5M users in two groups of countries with high Telegram penetration:"
          className="mb-3"
          descriptionClassName="!text-base text-neutral-600"
        />
        <List
          items={marketStrategy}
          description="Go-to-Market Strategy:"
          descriptionClassName="!text-base text-neutral-600"
        />
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <p className="mb-4 -mt-2 font-semibold">
          Our Team: We have a team with strong product and startup experience. Key members include:
        </p>
        <TeamMemberList
          className="!grid-cols-1 !items-start max-md:flex-col"
          cardClassName="!items-start max-md:flex-col"
          teamMembers={teamMembers}
        />
        <p className="my-4 font-semibold">
          Our team is distributed, with key members in Indonesia, Bali, Barcelona, and Georgia. Our
          advisory board includes the Telespace Team, the top Telegram Promotion agency, and Jan
          Richter, head of Product at ZeptoLab.
        </p>
        <ImageProxy
          alt="Team"
          src="/launchpad/stories/fanton/team.png"
          className="w-full h-auto rounded-lg object-contain shadow-main"
          quality={80}
          width={1920}
          height={1080}
        />
      </>
    ),
  },
  {
    id: 'tokenomics',
    title: 'Tokenomics',
    content: (
      <>
        <ImageProxy
          alt="Tokenomics diagram: 20.00% Play-t-earn, 26.25% Ecosystem Treasury & Liquidity, 5.0% Advisors, 6.00% Pre-seed Round, 15% Community / Marketing, 7.00% Seed Round, 1.50% KOLs round, 1.75% Public Sale, 17.50% Founders & Team"
          src="/launchpad/stories/fanton/fanton-tokenomics.png"
          className="w-full h-auto rounded-lg object-contain shadow-main hidden sm:block"
          quality={80}
          width={820}
          height={264}
        />
        <ImageProxy
          alt="Tokenomics diagram: 20.00% Play-t-earn, 26.25% Ecosystem Treasury & Liquidity, 5.0% Advisors, 6.00% Pre-seed Round, 15% Community / Marketing, 7.00% Seed Round, 1.50% KOLs round, 1.75% Public Sale, 17.50% Founders & Team"
          src="/launchpad/stories/fanton/fanton-tokenomics-mobile.png"
          className="w-full h-auto rounded-lg object-contain block sm:hidden shadow-main"
          quality={80}
          width={272}
          height={526}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink
            className="underline"
            href="https://docs.google.com/spreadsheets/d/1jPWcJJqo4Ou5z8VWgFPeiUwuUDS1mW9Xp1PF529VT8E/edit#gid=1763394760"
          >
            Tokenomics
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'token-utility',
    title: 'Token Utility',
    content: (
      <>
        <List items={tokenUtility} />
      </>
    ),
  },
  {
    id: 'token-deflation',
    title: 'Token Deflation',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm">
          Fanton employs a unique mechanism to stabilize the token price by using profits from paid
          tournaments to buy Fanton Tokens from the market and transferring these tokens to an
          inactive address. This "token burning" mechanism helps maintain a stable token price and
          ensures a seamless gaming experience. Additionally, the tokens used for tournament entry
          fees are periodically burned to decrease supply and increase demand.
        </p>
        <div className="overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {tokenUtilityGallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-4/5 px-1')}>
                <ImageProxy
                  alt=""
                  width={640}
                  height={360}
                  quality={80}
                  className="w-full h-auto rounded-lg select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'project-roadmap',
    title: 'Project Roadmap',
    content: (
      <>
        <List items={roadmap} />
        <List items={goals} className="mb-4 ml-4" />
        <ExternalLink className="text-primary-600" href="https://fan-ton.com/vision">
          https://fan-ton.com/vision
        </ExternalLink>
      </>
    ),
  },
];
